import {
    BackgroundCheckHistoryColumns,
    BackgroundCheckHistoryData,
    backgroundCheckHistoryEntityName,
} from '@/backend/features/background-check';
import { BackgroundCheckColumns } from '@/backend/features/background-check/shared';
import { BackgroundCheckStatus } from '@/backend/features/background-check/types';
import { ExtendedAccountData } from '@/backend/features/contact-management/entities/extended-account/types';
import { BackgroundCheckHistoryTable } from '@/ui/features/background-check/components/background-check-user-account-section/components/BackgroundCheckHistoryTable';
import { collectFilesAndFilterData } from '@juvare/common-form-ui/WidgetFormFile/utils';
import { showFormDialog } from '@juvare/common-form-ui/showFormDialog';
import { handleErrorDialog, showProgressDialog } from '@juvare/common-web-ui/DialogHelper';
import { themeColors } from '@juvare/common-web-ui/JuvareTheme';
import { showInfoSnackbar } from '@juvare/common-web-ui/SnackbarHelper';
import { useAttachmentClient, useStorageClient } from '@juvare/platform-sdk-react/ui/hooks';
import { getAttachmentUrl } from '@juvare/platform-sdk-react/ui/utils';
import { ListResponse } from '@juvare/platform-shared/features/storage';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Button, Collapse, IconButton, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';

export type BackgroundCheckHistoryExpandableRowProps = {
    accountId: string;
    accountData: ExtendedAccountData;
    backgroundCheck: BackgroundCheckColumns;
    size: number;
    backgroundCheckHistoryPages: ListResponse<BackgroundCheckHistoryColumns>[];
    setSize: (size: number | ((_size: number) => number)) => unknown;
    mutate: () => Promise<unknown>;
    isLastPage: boolean;
    isValidating: boolean;
    total: number;
    isOwnAccount: boolean;
};

export const BackgroundCheckHistoryExpandableRow: React.FC<BackgroundCheckHistoryExpandableRowProps> = ({
    accountId,
    accountData,
    backgroundCheck,
    setSize,
    mutate,
    backgroundCheckHistoryPages,
    isLastPage,
    isValidating,
    isOwnAccount,
    total,
}) => {
    const [historyExpanded, setHistoryExpanded] = React.useState(false);

    const { t } = useTranslation('rms');
    const attachmentStorage = useAttachmentClient();
    const backgroundCheckHistoryClient = useStorageClient<BackgroundCheckHistoryData, BackgroundCheckHistoryColumns>(
        backgroundCheckHistoryEntityName,
    );

    const handleShowMore = async () => {
        await setSize((previousSize) => previousSize + 1);
    };

    const handleAddNote = () => {
        if (!accountId || isOwnAccount) {
            return;
        }

        showFormDialog({
            title: t('backgroundCheck.labels.addNote'),
            data: {
                backgroundCheckType: backgroundCheck.type,
            },
            config: {
                type: 'form',
                children: [
                    {
                        type: 'form.panel',
                        children: [
                            {
                                type: 'form.select',
                                key: 'backgroundCheckType',
                                label: t('backgroundCheck.labels.backgroundCheckType'),
                                hidden: Boolean(backgroundCheck.type),
                                valueProvider: {
                                    type: 'backgroundCheckEnabledTypes',
                                },
                                validate: {
                                    required: true,
                                },
                            },
                            {
                                type: 'form.text',
                                key: 'note',
                                rows: 3,
                                label: t('backgroundCheck.labels.backgroundCheckNotes'),
                                validate: {
                                    required: true,
                                },
                                multiline: true,
                            },
                            {
                                type: 'form.file',
                                key: 'document',
                                label: t('backgroundCheck.labels.uploadBackgroundCheckDocument'),
                                labels: {
                                    uploadLabel: t('backgroundCheck.labels.uploadDocument'),
                                },
                                acceptTypes:
                                    'image/png, application/pdf, image/jpeg, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                                multipleFiles: false,
                            },
                            {
                                type: 'form.content',
                                html: '<hr>',
                                style: {},
                            },
                        ],
                    },
                ],
            },
            onSubmit: (data) => {
                backgroundCheckHistoryClient
                    .post({
                        data: {
                            accountId: accountId,
                            type: data.backgroundCheckType,
                            note: data.note,
                            prevStatus: backgroundCheck?.status as BackgroundCheckStatus,
                            nextStatus: backgroundCheck?.status as BackgroundCheckStatus,
                        },
                        action: 'create',
                    })
                    .then(async (response) => {
                        const generateAttachmentUrl = async (key: string) => {
                            return getAttachmentUrl({
                                entity: backgroundCheckHistoryEntityName,
                                entityId: response.id,
                                key,
                            });
                        };

                        const { files, submitData } = await collectFilesAndFilterData(data, generateAttachmentUrl);

                        if (files.length !== 0) {
                            showProgressDialog({
                                title: t('backgroundCheck.labels.uploadingFiles'),
                                callback: async (done: () => void) => {
                                    for (const { entityKey, file } of files) {
                                        try {
                                            await attachmentStorage.post({
                                                entity: backgroundCheckHistoryEntityName,
                                                entityId: response.id,
                                                key: entityKey,
                                                contentType: file.type ? file.type : 'application/octet-stream',
                                                contentLength: file.size,
                                                content: file,
                                            });
                                        } catch (e: unknown) {
                                            handleErrorDialog(e);
                                        } finally {
                                            await backgroundCheckHistoryClient.post({
                                                action: 'partial-update',
                                                id: response.id,
                                                data: {
                                                    document:
                                                        submitData.document && submitData.document.length !== 0 && submitData.document[0],
                                                },
                                            });
                                        }
                                    }
                                    done();
                                },
                            });
                        }
                        await mutate();
                    });

                showInfoSnackbar({
                    message: t('backgroundCheck.messages.noteAdded'),
                    autoHideDuration: 2000,
                });
            },
        });
    };

    return (
        <>
            <Box sx={{ mt: 2, backgroundColor: themeColors.surface.secondary }} data-test='background-check-history-and-notes-section'>
                <Box
                    data-test='row-header'
                    onClick={() => setHistoryExpanded((expanded) => !expanded)}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                    }}
                >
                    <Box
                        sx={{
                            p: 1,
                            pr: 0,
                        }}
                    >
                        <IconButton
                            color='primary'
                            sx={{
                                transition: (theme) => theme.transitions.create(['transform']),
                                transform: !historyExpanded ? 'rotate(0deg)' : 'rotate(-180deg)',
                            }}
                            aria-label={t('backgroundCheck.labels.toggleBackgroundCheckHistory')}
                            data-test='toggle-background-check-history-button'
                            onClick={(e) => {
                                e.stopPropagation();
                                setHistoryExpanded((expanded) => !expanded);
                            }}
                        >
                            <ExpandMoreIcon />
                        </IconButton>
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            p: 1,
                            pr: 2,
                            flexGrow: 1,
                            alignSelf: 'center',
                        }}
                    >
                        <Typography variant='subtitle2' sx={{ flexGrow: { sm: 1 }, pt: { sm: 1 }, pb: { sm: 1 } }}>
                            {t('backgroundCheck.labels.historyAndNotes', { total })}
                        </Typography>
                        {accountId && accountData && !isOwnAccount && (
                            <Button
                                startIcon={<AddIcon />}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleAddNote();
                                }}
                                variant='text'
                                data-test='add-note-button'
                            >
                                {t('backgroundCheck.labels.addNote')}
                            </Button>
                        )}
                    </Box>
                </Box>
            </Box>
            <Collapse in={historyExpanded}>
                <Box
                    data-test='history-and-notes-row-body'
                    sx={{
                        p: 2,
                        pt: 0,
                        bgcolor: themeColors.surface.secondary,
                    }}
                >
                    <BackgroundCheckHistoryTable
                        backgroundCheckHistoryPages={backgroundCheckHistoryPages ?? []}
                        isLastPage={isLastPage}
                        onShowMore={handleShowMore}
                        isValidating={isValidating}
                    />
                </Box>
            </Collapse>
        </>
    );
};
